// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, Heart, Search, Menu, X, User, LogOut, Package, Store, Settings } from 'lucide-react';
import { useApp } from '../context/AppContext';

export default function Navbar() {
  const { user, logout, cartCount, wishlist, setCartOpen } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenu,   setUserMenu]   = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setUserMenu(false); }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const navLinks = [
    { to: '/',                              label: 'Home'         },
    { to: '/products',                      label: 'Shop'         },
    { to: '/products?category=personalized', label: 'Personalized' },
    { to: '/products?category=surprise',    label: 'Surprise Box' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-soft border-b border-rose-100'
          : 'bg-white border-b border-rose-100'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center shadow-rose animate-heartbeat">
                <span className="text-white text-xs font-bold">♡</span>
              </div>
              <span className="font-display font-bold text-xl text-gray-900 tracking-tight">
                Couple<span className="text-rose-500">Cart</span>
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to}
                  className={`font-body text-sm font-medium transition-colors hover:text-rose-500 ${
                    location.pathname === link.to ? 'text-rose-500' : 'text-gray-600'
                  }`}>
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              <button onClick={() => setSearchOpen(true)}
                className="p-2 text-gray-600 hover:text-rose-500 hover:bg-rose-50 rounded-full transition-all"
                aria-label="Search">
                <Search size={20} />
              </button>

              {user && (
                <Link to="/wishlist"
                  className="relative p-2 text-gray-600 hover:text-rose-500 hover:bg-rose-50 rounded-full transition-all"
                  aria-label="Wishlist">
                  <Heart size={20} />
                  {wishlist.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                      {wishlist.length}
                    </span>
                  )}
                </Link>
              )}

              <button onClick={() => setCartOpen(true)}
                className="relative p-2 text-gray-600 hover:text-rose-500 hover:bg-rose-50 rounded-full transition-all"
                aria-label="Cart">
                <ShoppingBag size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold animate-pulse">
                    {cartCount}
                  </span>
                )}
              </button>

              {user ? (
                <div className="relative">
                  <button onClick={() => setUserMenu(!userMenu)}
                    className="flex items-center gap-2 p-1.5 hover:bg-rose-50 rounded-full transition-all">
                    <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {user.displayName?.[0]?.toUpperCase() || 'U'}
                    </div>
                  </button>
                  {userMenu && (
                    <div className="absolute right-0 top-12 w-52 bg-white rounded-2xl shadow-card border border-rose-100 py-2 animate-fade-in z-50">
                      <div className="px-4 py-2 border-b border-rose-50">
                        <p className="text-xs text-gray-400 font-body">Signed in as</p>
                        <p className="text-sm font-medium text-gray-800 truncate">{user.displayName}</p>
                      </div>
                      <Link to="/orders"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:text-rose-500 hover:bg-rose-50 transition-colors">
                        <Package size={16} /> My Orders
                      </Link>
                      {/* Show Become Merchant only for regular users */}
                      {(!user.role || user.role === 'user') && (
                        <Link to="/become-merchant"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:text-rose-500 hover:bg-rose-50 transition-colors border-t border-rose-50">
                          <Store size={16} /> Become a Merchant
                        </Link>
                      )}
                      {(user.role === 'merchant' || user.role === 'admin') && (
                        <Link to="/merchant"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-rose-600 hover:text-rose-700 hover:bg-rose-50 transition-colors font-semibold border-t border-rose-100">
                          <Store size={16} /> Merchant Dashboard
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/admin"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-colors font-semibold">
                          <Settings size={16} /> Admin Panel
                        </Link>
                      )}
                      <button onClick={logout}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:text-rose-500 hover:bg-rose-50 transition-colors border-t border-rose-50">
                        <LogOut size={16} /> Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login"
                  className="hidden md:flex items-center gap-1.5 bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-all shadow-rose hover:shadow-rose-lg">
                  <User size={16} /> Login
                </Link>
              )}

              <button className="md:hidden p-2 text-gray-600 hover:bg-rose-50 rounded-full transition-all"
                onClick={() => setMobileOpen(!mobileOpen)}>
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileOpen && (
            <div className="md:hidden py-4 border-t border-rose-100 animate-slide-up">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to}
                  className="block px-2 py-3 text-gray-600 hover:text-rose-500 font-body font-medium transition-colors">
                  {link.label}
                </Link>
              ))}
              {!user && (
                <Link to="/login"
                  className="block mt-3 text-center bg-rose-500 text-white px-4 py-3 rounded-2xl font-medium">
                  Login / Sign Up
                </Link>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
          onClick={() => setSearchOpen(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-xl shadow-2xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}>
            <p className="text-sm text-gray-400 mb-3 font-body">What are you looking for?</p>
            <form onSubmit={handleSearch} className="flex gap-3">
              <input autoFocus type="text" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search gifts, keychains, surprises…"
                className="flex-1 px-4 py-3 rounded-2xl border border-rose-200 focus:outline-none focus:border-rose-400 font-body text-gray-800 bg-rose-50/50" />
              <button type="submit"
                className="bg-rose-500 text-white px-5 py-3 rounded-2xl hover:bg-rose-600 transition-colors">
                <Search size={18} />
              </button>
            </form>
            <div className="mt-4 flex flex-wrap gap-2">
              {['Star Map', 'Love Jar', 'Surprise Box', 'Anniversary', 'Bookmark'].map((tag) => (
                <button key={tag}
                  onClick={() => { setSearchQuery(tag); navigate(`/products?search=${encodeURIComponent(tag)}`); setSearchOpen(false); }}
                  className="px-3 py-1.5 bg-rose-50 text-rose-500 rounded-full text-xs font-medium hover:bg-rose-100 transition-colors">
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
